

// 1. Mal girişi
// Ürün geldiğinde mutlaka kaydedin.
// Malzeme, Birimi, Tedarikçi, Tarih, Alış fiyatı

// 2. Günlük çıkış
// Örn:
// Pirinç → 6 kg, Tavuk → 8 kg, Domates → 12 kg

// 3. Haftalık sayım
// Haftada bir gün depodaki gerçek miktarla kayıtları karşılaştırın.



import React, { useState, useEffect, useCallback, useMemo } from 'react';

import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import { colors, radius, shadows } from '../../styles/theme';
import { formatSayi } from '../../utils/analytics/format';
import { exportCsv } from '../../utils/analytics/export';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};


const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getAprilFirstOfCurrentYear = () => {
  const today = new Date();
  return toDateInputValue(new Date(today.getFullYear(), 6, 1));
};
// TARİH BURASI fakat ay numarası bir eksi yazmak gerekiyor? Eski hali 4 , 21

const getTodayInputValue = () => toDateInputValue(new Date());

const addDays = (dateString, days) => {
  const [year, month, day] = dateString.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  d.setDate(d.getDate() + days);
  return toDateInputValue(d);
};

const getDateRange = (start, end) => {
  const dates = [];
  let current = start;
  while (current <= end) {
    dates.push(current);
    current = addDays(current, 1);
  }
  return dates;
};

const getMalzemeKey = (kalem) => String(kalem?.malzeme_id ?? kalem?.id ?? kalem?.malzeme_ad ?? '');

const TuketimRaporuPage = () => {
  useAuth();

  const [data, setData] = useState(null);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const today = new Date();
  const [baslangic, setBaslangic] = useUrlState('baslangic', '2026-07-01');
  const [bitis, setBitis] = useUrlState('bitis', today.toISOString().split('T')[0]);
  const [lokasyonId, setLokasyonId] = useUrlState('lokasyon', '');
  const [kategori, setKategori] = useUrlState('kategori', '');
  const [kategoriler, setKategoriler] = useState([]);
  const [seciliMalzemeKey, setSeciliMalzemeKey] = useState('');
  const [gunlukExportLoading, setGunlukExportLoading] = useState(false);
  const [tumMalzemeSecenekleri, setTumMalzemeSecenekleri] = useState([]);
  const [malzemeListeLoading, setMalzemeListeLoading] = useState(false);
  const [gunlukGrafikData, setGunlukGrafikData] = useState([]);
  const [gunlukGrafikLoading, setGunlukGrafikLoading] = useState(false);

  useEffect(() => {
    const fetchLokasyonlar = async () => {
      try {
        const res = await fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() });
        if (res.ok) {
          const result = await res.json();
          setLokasyonlar(result);
          if (result.length > 0 && !lokasyonId) {
            setLokasyonId(String(result[0].id));
          }
        }
      } catch (err) {
        logError('Lokasyonlar yuklenemedi:', err);
      }
    };
    fetchLokasyonlar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRapor = useCallback(async () => {
    if (!lokasyonId) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const params = new URLSearchParams({
        lokasyon_id: String(lokasyonId),
        baslangic,
        bitis,
      });

      if (kategori) params.set('kategori', kategori);

      const seciliMalzeme = malzemeSecenekleri.find(k => getMalzemeKey(k) === seciliMalzemeKey);
      if (seciliMalzemeKey && seciliMalzeme) {
        params.set('malzeme_id', String(seciliMalzeme.malzeme_id || seciliMalzeme.id));
      }

      const res = await fetch(`${API_URL}/api/raporlar/tuketim?${params.toString()}`, {
        headers: getAuthHeaders(),
      });

      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Rapor yuklenemedi');
      }
    } catch (err) {
      setError('Sunucu hatasi');
      logError('Tuketim raporu yuklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [lokasyonId, baslangic, bitis, kategori, seciliMalzemeKey, tumMalzemeSecenekleri]);

  // Lokasyon/filtre değişince otomatik yükle (fetchRapor lokasyon yokken no-op) —
  // önceden yalnızca butonla tetiklendiği için sayfa boş açılıyordu.
  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  // Kategoriler DB'den (Anayasa M.1) — hardcode yerine aktif malzeme kategorileri.
  // DB'de gerçekte ne varsa (Kasap Ürünü, Genel vb. dahil) onu listeler.
  useEffect(() => {
    fetch(`${API_URL}/api/malzeme-kategorileri`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setKategoriler(Array.isArray(d) ? d : []))
      .catch(() => setKategoriler([]));
  }, []);

  const rebuildRapor = async () => {
    setRebuildLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(`${API_URL}/api/admin/rapor-tuketim-rebuild`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Rapor hesaplanamadı');
      }

      const result = await res.json();
      setSuccess(result.message || 'Tüketim raporu hesaplandı ve kaydedildi');

      await fetchTumMalzemeSecenekleri();
      await fetchRapor();
    } catch (err) {
      setError(err.message || 'Rapor hesaplanırken hata oluştu');
      logError('Tuketim raporu rebuild hatasi:', err);
    } finally {
      setRebuildLoading(false);
    }
  };

  const fetchTumMalzemeSecenekleri = useCallback(async () => {
    if (!lokasyonId) return;

    //const start = getAprilFirstOfCurrentYear();
    const start = '2026-07-01';
    const end = getTodayInputValue();

    setMalzemeListeLoading(true);
    try {
      const params = new URLSearchParams({
        lokasyon_id: String(lokasyonId),
        baslangic,
        bitis,
      });
      if (kategori) params.set('kategori', kategori);

      const res = await fetch(`${API_URL}/api/raporlar/tuketim?${params.toString()}`, {
        headers: getAuthHeaders(),
      });

      if (!res.ok) throw new Error('Malzeme listesi alınamadı');

      const result = await res.json();
      const map = new Map();

      (result.kalemler || []).forEach(kalem => {
        const key = getMalzemeKey(kalem);
        if (!key) return;
        map.set(key, kalem);
      });

      setTumMalzemeSecenekleri(
        Array.from(map.values()).sort((a, b) =>
          String(a.malzeme_ad || '').localeCompare(String(b.malzeme_ad || ''), 'tr')
        )
      );
    } catch (err) {
      setTumMalzemeSecenekleri([]);
      logError('Tum malzeme listesi yuklenemedi:', err);
    } finally {
      setMalzemeListeLoading(false);
    }
  }, [lokasyonId, kategori, bitis]);

  useEffect(() => {
    fetchTumMalzemeSecenekleri();
  }, [fetchTumMalzemeSecenekleri]);

  const containerStyle = {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  };

  const cardStyle = {
    background: colors.surface,
    borderRadius: radius.lg,
    padding: '24px',
    boxShadow: shadows.sm,
    marginBottom: '20px',
  };

  const filterRowStyle = {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    marginBottom: '20px',
  };

  const fieldStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const labelStyle = {
    fontSize: '13px',
    fontWeight: '600',
    color: '#555',
  };

  const inputStyle = {
    padding: '8px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.md,
    fontSize: '14px',
    outline: 'none',
  };

  const btnStyle = {
    padding: '8px 20px',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: radius.md,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  };

  const thStyle = {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: '600',
    fontSize: '13px',
    color: '#555',
    borderBottom: `2px solid ${colors.border}`,
    position: 'sticky',
    top: 0,
    background: colors.surface,
    whiteSpace: 'nowrap',
  };

  const thRightStyle = { ...thStyle, textAlign: 'right' };

  const tdStyle = {
    padding: '8px 12px',
    fontSize: '13px',
    borderBottom: '1px solid #f3f3f3',
  };

  const tdRightStyle = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  // Son yeşil Depo noktası ve yalnızca kayıtlı ürün dönüşümü.
  const sonDurum = useMemo(() => {
    if (!seciliMalzemeKey || !gunlukGrafikData.length) return null;
    const son = gunlukGrafikData[gunlukGrafikData.length - 1];
    const miktar = Number(son?.Depo);
    if (!Number.isFinite(miktar)) return null;
    const birim = String(son?.Birim || tumMalzemeSecenekleri.find(k => getMalzemeKey(k) === seciliMalzemeKey)?.birim || '').trim().toLocaleUpperCase('tr-TR');
    const adet = son?.depo_adet;
    if (['KG', 'KİLOGRAM', 'KILOGRAM'].includes(birim)) {
      return { kg: miktar, adet: adet == null || !Number.isFinite(Number(adet)) ? null : Number(adet) };
    }
    if (['G', 'GR', 'GRAM'].includes(birim)) return { kg: miktar / 1000, adet: null };
    if (['AD', 'ADET'].includes(birim)) return { adet: miktar, kg: null };
    return { miktar, birim: birim || 'birim belirtilmemiş' };
  }, [seciliMalzemeKey, gunlukGrafikData, tumMalzemeSecenekleri]);

  const malzemeSecenekleri = tumMalzemeSecenekleri;

const fetchSeciliMalzemeGunlukRows = useCallback(async () => {
  if (!lokasyonId || !seciliMalzemeKey) return [];

  const seciliMalzeme = malzemeSecenekleri.find(k => getMalzemeKey(k) === seciliMalzemeKey);
  if (!seciliMalzeme) return [];

  const start = baslangic || '2026-07-01';
  const end = bitis || getTodayInputValue();

  const params = new URLSearchParams({
    lokasyon_id: String(lokasyonId),
    malzeme_id: String(seciliMalzeme.malzeme_id || seciliMalzeme.id),
    baslangic: start,
    bitis: end,
  });

  const res = await fetch(`${API_URL}/api/raporlar/tuketim-gunluk?${params.toString()}`, {
    headers: getAuthHeaders(),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Günlük rapor verisi alınamadı');
  }

  const result = await res.json();
  const rows = Array.isArray(result) ? result : (result.kalemler || result.rows || []);

  return rows.map(r => ({
    Tarih: String(r.rapor_tarihi || r.tarih || '').slice(0, 10),
    Malzeme: r.malzeme_ad || seciliMalzeme.malzeme_ad || '',
    Kod: r.malzeme_kod || r.kod || seciliMalzeme.kod || '',
    Birim: r.birim || seciliMalzeme.birim || '',
    Kategori: r.kategori || seciliMalzeme.kategori || '',
    'Dönem Başı': Number(r.donem_basi_stok || 0),
    Giriş: Number(r.donem_ici_giris || 0),
    Çıkış: Number(r.donem_ici_cikis || 0),
    'Normal Çıkış': Number(r.cikis || 0),
    'Üretim Çıkış': Number(r.uretim_cikis || 0),
    'Transfer Çıkış': Number(r.transfer_cikis || 0),
    Satış: Number(r.satis || 0),
    'Dış Satış': Number(r.satis_dis || 0),
    Fire: Number(r.fire || 0),
    Zayi: Number(r.zayi || 0),
    İkram: Number(r.ikram || 0),
    'Personel Tük.': Number(r.personel_tuketim || 0),
    İade: Number(r.iade || 0),
    ModPos: Number(r.tuketim || 0),
    Depo: Number(r.donem_sonu_stok || 0),
    depo_adet: r.depo_adet == null ? null : Number(r.depo_adet),
    adet_kg_gramaj: r.adet_kg_gramaj == null ? null : Number(r.adet_kg_gramaj),
    'Dönem Sonu': Number(r.donem_sonu_stok || 0),
    Tüketim: Number(r.tuketim || 0),
    'Günlük Ort.': Number(r.gunluk_ortalama || 0),
  }));
}, [lokasyonId, seciliMalzemeKey, malzemeSecenekleri, baslangic, bitis]);


  useEffect(() => {
    let cancelled = false;

    const loadGrafik = async () => {
      if (!seciliMalzemeKey) {
        setGunlukGrafikData([]);
        return;
      }

      setGunlukGrafikLoading(true);
      setError(null);

      try {
        const rows = await fetchSeciliMalzemeGunlukRows();
        if (!cancelled) setGunlukGrafikData(rows);
      } catch (err) {
        if (!cancelled) {
          setGunlukGrafikData([]);
          setError(err.message || 'Günlük grafik verisi alınamadı');
        }
        logError('Gunluk grafik verisi alinamadi:', err);
      } finally {
        if (!cancelled) setGunlukGrafikLoading(false);
      }
    };

    loadGrafik();

    return () => {
      cancelled = true;
    };
  }, [seciliMalzemeKey, fetchSeciliMalzemeGunlukRows]);

  const exportSeciliMalzemeGunlukCsv = async () => {
    if (!lokasyonId || !seciliMalzemeKey) return;

    const seciliMalzeme = malzemeSecenekleri.find(k => getMalzemeKey(k) === seciliMalzemeKey);
    if (!seciliMalzeme) {
      setError('Seçili malzeme listede bulunamadı. Malzeme listesini yenileyip tekrar deneyin.');
      return;
    }

    const start = getAprilFirstOfCurrentYear();
    const end = getTodayInputValue();

    setGunlukExportLoading(true);
    setError(null);

    try {
      const rows = await fetchSeciliMalzemeGunlukRows();

      const safeName = String(seciliMalzeme.malzeme_ad || 'malzeme')
        .trim()
        .replace(/[^a-zA-Z0-9ğüşöçıİĞÜŞÖÇ_.-]+/g, '-')
        .replace(/-+/g, '-');

      exportCsv(rows, `gunluk-malzeme-${safeName}-${start}-${end}.csv`);
    } catch (err) {
      setError(err.message || 'Günlük malzeme CSV export sırasında hata oluştu');
      logError('Gunluk malzeme CSV export hatasi:', err);
    } finally {
      setGunlukExportLoading(false);
    }
  };

  const SeciliMalzemeGrafik = () => {
    const chart = useMemo(() => {
      const width = Math.max(900, gunlukGrafikData.length * 14);
      const height = 380;
      const padding = 48;
      const plotWidth = width - padding * 2;
      const plotHeight = height - padding * 2;

      const values = gunlukGrafikData.flatMap(r => [
        Number(r.Giriş || 0),
        Number(r.ModPos || 0),
        Number(r.Depo || 0),
      ]);

      const rawMaxValue = Math.max(0, ...values);
      const rawMinValue = Math.min(0, ...values);

      // Dikey eksen üst sınırı Giriş / ModPos / Depo içindeki en büyük değere göre hesaplanır.
      // Sabit 100 gibi bir değer kullanılmaz. Üstte küçük bir pay bırakmak için %10 eklenir.
      const maxValue = rawMaxValue <= 0 ? 10 : rawMaxValue * 1.1;
      const minValue = rawMinValue < 0 ? rawMinValue * 1.1 : 0;
      const range = maxValue - minValue || 1;

      const x = index => {
        if (gunlukGrafikData.length <= 1) return padding;
        return padding + (index * plotWidth) / (gunlukGrafikData.length - 1);
      };

      const y = value => {
        return height - padding - ((value - minValue) * plotHeight) / range;
      };

      const linePath = key =>
        gunlukGrafikData
          .map((r, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(r[key])}`)
          .join(' ');

      return { width, height, padding, minValue, maxValue, x, y, linePath };
    }, [gunlukGrafikData]);

    if (!seciliMalzemeKey) return null;

    const seciliMalzeme = malzemeSecenekleri.find(k => getMalzemeKey(k) === seciliMalzemeKey);
    const grafikBirim = seciliMalzeme?.birim || gunlukGrafikData.find(r => r.Birim)?.Birim || '';
    const formatTarih = tarih => String(tarih || '').split('-').reverse().join('.');
    const tooltipText = (tarih, etiket, deger) => `${formatTarih(tarih)}\n${etiket}: ${formatSayi(deger)}${grafikBirim ? ` ${grafikBirim}` : ''}`;

    return (
      <div style={{
        marginTop: '18px',
        marginBottom: '18px',
        padding: '18px',
        border: `1px solid ${colors.border}`,
        borderRadius: radius.md,
        background: '#fff',
      }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#1e293b' }}>
          {seciliMalzeme?.malzeme_ad || 'Seçili Malzeme'} Günlük Grafik
        </h3>

        {gunlukGrafikLoading ? (
          <div style={{ padding: '24px', color: '#64748b' }}>Grafik hazırlanıyor...</div>
        ) : gunlukGrafikData.length === 0 ? (
          <div style={{ padding: '24px', color: '#64748b' }}>Grafik için veri bulunamadı.</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', fontSize: '13px' }}>
              <span style={{ color: '#2563eb', fontWeight: '600' }}>● Giriş</span>
              <span style={{ color: '#dc2626', fontWeight: '600' }}>● ModPos</span>
              <span style={{ color: '#2EF527', fontWeight: '600' }}>● Depo</span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <svg width={chart.width} height={chart.height} style={{ background: '#fff' }}>
                {[0, 1, 2, 3, 4].map(i => {
                  const value = chart.minValue + ((chart.maxValue - chart.minValue) * i) / 4;
                  const yy = chart.y(value);
                  return (
                    <g key={i}>
                      <line
                        x1={chart.padding}
                        y1={yy}
                        x2={chart.width - chart.padding}
                        y2={yy}
                        stroke="#e5e7eb"
                      />
                      <text x="8" y={yy + 4} fontSize="11" fill="#64748b">
                        {formatSayi(value)}
                      </text>
                    </g>
                  );
                })}

                <line
                  x1={chart.padding}
                  y1={chart.padding}
                  x2={chart.padding}
                  y2={chart.height - chart.padding}
                  stroke="#cbd5e1"
                />
                <line
                  x1={chart.padding}
                  y1={chart.height - chart.padding}
                  x2={chart.width - chart.padding}
                  y2={chart.height - chart.padding}
                  stroke="#cbd5e1"
                />

                <path d={chart.linePath('Giriş')} fill="none" stroke="#2563eb" strokeWidth="2" />
                <path d={chart.linePath('ModPos')} fill="none" stroke="#dc2626" strokeWidth="2" />
                <path d={chart.linePath('Depo')} fill="none" stroke="#2EF527" strokeWidth="2" />

                {gunlukGrafikData.map((r, i) => (
                  <g key={`points-${r.Tarih}`}>
                    <circle cx={chart.x(i)} cy={chart.y(r.Giriş)} r="3" fill="#2563eb">
                      <title>{tooltipText(r.Tarih, 'Giriş', r.Giriş)}</title>
                    </circle>
                    <circle cx={chart.x(i)} cy={chart.y(r.ModPos)} r="3" fill="#dc2626">
                      <title>{tooltipText(r.Tarih, 'ModPos', r.ModPos)}</title>
                    </circle>
                    <circle cx={chart.x(i)} cy={chart.y(r.Depo)} r="3" fill="#2EF527">
                      <title>{tooltipText(r.Tarih, 'Depo', r.Depo)}</title>
                    </circle>
                  </g>
                ))}

                {gunlukGrafikData.map((r, i) => {
                  if (i % 7 !== 0 && i !== gunlukGrafikData.length - 1) return null;
                  return (
                    <text
                      key={`date-${r.Tarih}`}
                      x={chart.x(i)}
                      y={chart.height - 18}
                      fontSize="10"
                      fill="#64748b"
                      textAnchor="middle"
                    >
                      {formatTarih(r.Tarih)}
                    </text>
                  );
                })}

                <text
                  x={18}
                  y={chart.height / 2}
                  fontSize="12"
                  fill="#64748b"
                  transform={`rotate(-90 18 ${chart.height / 2})`}
                  textAnchor="middle"
                >
                  {grafikBirim || 'Birim'}
                </text>
              </svg>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1e293b' }}>
          Tuketim Raporu
        </h2>

        <div style={filterRowStyle}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Lokasyon</label>
            <select
              style={{ ...inputStyle, minWidth: '180px' }}
              value={lokasyonId}
              onChange={e => setLokasyonId(e.target.value)}
            >
              <option value="">Lokasyon secin</option>
              {lokasyonlar.map(l => (
                <option key={l.id} value={l.id}>{l.ad}</option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Başlangıç</label>
            <input
              type="date"
              style={inputStyle}
              value={baslangic}
              onChange={e => setBaslangic(e.target.value)}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Bitiş</label>
            <input
              type="date"
              style={inputStyle}
              value={bitis}
              onChange={e => setBitis(e.target.value)}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Kategori</label>
            <select
              style={{ ...inputStyle, minWidth: '140px' }}
              value={kategori}
              onChange={e => setKategori(e.target.value)}
            >
              <option value="">Tumu</option>
              {kategoriler.map(k => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Malzeme</label>
            <select
              style={{ ...inputStyle, minWidth: '240px' }}
              value={seciliMalzemeKey}
              onChange={e => setSeciliMalzemeKey(e.target.value)}
            >
              <option value="">{malzemeListeLoading ? 'Malzemeler yükleniyor...' : 'Tüm malzemeler'}</option>
              {malzemeSecenekleri.map(k => (
                <option key={getMalzemeKey(k)} value={getMalzemeKey(k)}>
                  {k.malzeme_ad}
                </option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>&nbsp;</label>
            <button
              style={{ ...btnStyle, opacity: loading || !lokasyonId ? 0.6 : 1 }}
              onClick={fetchRapor}
              disabled={loading || !lokasyonId}
            >
              {loading ? 'Yukleniyor...' : 'Listele'}
            </button>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>&nbsp;</label>
            <button
              style={{ ...btnStyle, background: '#16a34a', opacity: rebuildLoading ? 0.6 : 1 }}
              onClick={rebuildRapor}
              disabled={rebuildLoading}
            >
              {rebuildLoading ? 'Hesaplanıyor...' : 'Hesapla ve Kaydet'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ padding: '12px', background: '#fef2f2', color: '#dc2626', borderRadius: radius.md, marginBottom: '16px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ padding: '12px', background: '#f0fdf4', color: '#166534', borderRadius: radius.md, marginBottom: '16px', fontSize: '14px' }}>
            {success}
          </div>
        )}

        {data && (
          <>
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '16px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}>
              <div style={{
                padding: '10px 20px',
                background: '#f0f9ff',
                borderRadius: radius.md,
                fontSize: '14px',
                color: '#1e40af',
                fontWeight: '600',
              }}>
                {data.lokasyon_ad || 'Lokasyon'} &mdash; {data.baslangic || baslangic} / {data.bitis || bitis}
              </div>
              <div style={{
                padding: '10px 20px',
                background: '#f0fdf4',
                borderRadius: radius.md,
                fontSize: '14px',
                color: '#166534',
                fontWeight: '600',
              }}>
                Son Durum : {gunlukGrafikLoading ? 'Yükleniyor...' : !sonDurum ? 'Grafikte veri yok' : sonDurum.birim ? `${formatSayi(sonDurum.miktar)} ${sonDurum.birim}` : [sonDurum.adet != null ? `${formatSayi(sonDurum.adet)} adet` : null, sonDurum.kg != null ? `${formatSayi(sonDurum.kg)} kg` : null].filter(Boolean).join(' ve ')}
              </div>
              {/* CSV export — diğer rapor sayfalarıyla tutarlılık (ortak exportCsv util'i) */}
              <button
                onClick={() => exportCsv((data.kalemler || []).map(k => ({
                  Malzeme: k.malzeme_ad,
                  Kod: k.kod || '',
                  Birim: k.birim || '',
                  Kategori: k.kategori || '',
                  'Dönem Başı': k.donem_basi_stok,
                  'Giriş': k.donem_ici_giris,
                  'Çıkış': k.donem_ici_cikis,
                  'Normal Çıkış': k.cikis,
                  'Üretim Çıkış': k.uretim_cikis,
                  'Transfer Çıkış': k.transfer_cikis,
                  'Satış': k.satis,
                  'Dış Satış': k.satis_dis,
                  'Fire': k.fire,
                  'Zayi': k.zayi,
                  'İkram': k.ikram,
                  'Personel Tük.': k.personel_tuketim,
                  'İade': k.iade,
                  'Dönem Sonu': k.donem_sonu_stok,
                  'Tüketim': k.tuketim,
                  'Günlük Ort.': k.gunluk_ortalama,
                })), `tuketim-raporu-${baslangic}-${bitis}.csv`)}
                style={{
                  marginLeft: 'auto', padding: '8px 16px', border: '1px solid #2563eb',
                  borderRadius: radius.md, background: '#fff', color: '#2563eb',
                  cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                }}
              >
                CSV Indir
              </button>

              <button
                onClick={exportSeciliMalzemeGunlukCsv}
                disabled={!seciliMalzemeKey || gunlukExportLoading || malzemeListeLoading}
                style={{
                  padding: '8px 16px', border: '1px solid #16a34a',
                  borderRadius: radius.md, background: '#fff', color: '#16a34a',
                  cursor: !seciliMalzemeKey || gunlukExportLoading || malzemeListeLoading ? 'not-allowed' : 'pointer',
                  fontSize: '13px', fontWeight: '600',
                  opacity: !seciliMalzemeKey || gunlukExportLoading || malzemeListeLoading ? 0.6 : 1,
                }}
              >
                {gunlukExportLoading ? 'Hazırlanıyor...' : 'Tamamı - CSV'}
              </button>
            </div>

            <SeciliMalzemeGrafik />

            <div style={{ overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Malzeme</th>
                    <th style={thStyle}>Kod</th>
                    <th style={thStyle}>Birim</th>
                    <th style={thStyle}>Kategori</th>
                    <th style={thRightStyle}>D. Başı</th>
                    <th style={thRightStyle}>Giriş</th>
                    <th style={thRightStyle}>Normal Çıkış</th>
                    <th style={thRightStyle}>Üretim Çıkış</th>
                    <th style={thRightStyle}>Transfer Çıkış</th>
                    <th style={thRightStyle}>Satış</th>
                    <th style={thRightStyle}>Dış Satış</th>
                    <th style={thRightStyle}>Fire</th>
                    <th style={thRightStyle}>Zayi</th>
                    <th style={thRightStyle}>İkram</th>
                    <th style={thRightStyle}>Personel Tük.</th>
                    <th style={thRightStyle}>İade</th>
                    <th style={thRightStyle}>Toplam Çıkış</th>
                    <th style={thRightStyle}>D. Sonu</th>
                    <th style={thRightStyle}>Tüketim</th>
                    <th style={thRightStyle}>Günlük Ort.</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.kalemler || []).length === 0 && (
                    <tr>
                      <td colSpan={20} style={{ ...tdStyle, textAlign: 'center', color: colors.textTertiary, padding: '24px' }}>
                        Bu donemde veri bulunamadi
                      </td>
                    </tr>
                  )}
                  {(data.kalemler || []).map((k, i) => {
                    const tuketimColor = k.tuketim < 0 ? '#dc2626' : '#1e293b';
                    return (
                      <tr key={k.malzeme_id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ ...tdStyle, fontWeight: '500' }}>{k.malzeme_ad}</td>
                        <td style={{ ...tdStyle, color: '#888', fontSize: '12px' }}>{k.malzeme_kod || k.kod}</td>
                        <td style={tdStyle}>{k.birim}</td>
                        <td style={tdStyle}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: radius.sm,
                            background: '#f1f5f9',
                            fontSize: '11px',
                            fontWeight: '500',
                          }}>
                            {k.kategori}
                          </span>
                        </td>
                        <td style={tdRightStyle}>{formatSayi(k.donem_basi_stok)}</td>
                        <td style={{ ...tdRightStyle, color: '#16a34a' }}>{formatSayi(k.donem_ici_giris)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.cikis)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.uretim_cikis)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.transfer_cikis)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.satis)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.satis_dis)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.fire)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.zayi)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.ikram)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.personel_tuketim)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.iade)}</td>
                        <td style={{ ...tdRightStyle, color: '#dc2626', fontWeight: '600' }}>{formatSayi(k.donem_ici_cikis)}</td>
                        <td style={tdRightStyle}>{formatSayi(k.donem_sonu_stok)}</td>
                        <td style={{ ...tdRightStyle, fontWeight: '700', color: tuketimColor }}>
                          {formatSayi(k.tuketim)}
                        </td>
                        <td style={tdRightStyle}>{formatSayi(k.gunluk_ortalama)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TuketimRaporuPage;
